/**
 * Parses a Google Place object and extracts address components
 * 
 * @param {Object} place - The Google Place object returned from the API
 * @returns {Object} Parsed address fields
 */
export const parseGoogleAddress = (place) => {
  if (!place || !place.address_components) {
    return {};
  }

  const addressComponents = place.address_components;
  
  // Find address components by type
  const getComponentByType = (type) => {
    const component = addressComponents.find((component) =>
      component.types.includes(type)
    );
    return component ? component.long_name : '';
  };

  const getComponentShortByType = (type) => {
    const component = addressComponents.find((component) =>
      component.types.includes(type)
    );
    return component ? component.short_name : '';
  };

  // Extract address parts
  const streetNumber = getComponentByType('street_number');
  const route = getComponentByType('route');
  const city = getComponentByType('locality') || 
               getComponentByType('sublocality_level_1') || 
               getComponentByType('administrative_area_level_3');
  
  // Get both short and long state names
  const stateShort = getComponentShortByType('administrative_area_level_1');
  const stateLong = getComponentByType('administrative_area_level_1');
  
  // Use long state name as it matches the dropdown state options format in the forms
  const state = stateLong || stateShort;
  
  const zipCode = getComponentByType('postal_code');
  const country = getComponentShortByType('country');

  // Build street address
  const streetAddress = [streetNumber, route]
    .filter(Boolean)
    .join(' ')
    .trim();

  return {
    streetAddress,
    city,
    state,
    zipCode,
    country,
    formattedAddress: place.formatted_address || '',
    
    // Return raw component data for advanced usage
    raw: {
      streetNumber,
      route,
      premise: getComponentByType('premise'),
      subpremise: getComponentByType('subpremise'),
      neighborhood: getComponentByType('neighborhood'),
      administrativeArea1: getComponentByType('administrative_area_level_1'),
      administrativeArea1Short: getComponentShortByType('administrative_area_level_1'),
      administrativeArea2: getComponentByType('administrative_area_level_2'),
      fullComponents: addressComponents
    }
  };
};

/**
 * Converts a Google Place object to a form data format
 * 
 * @param {Object} place - The Google Place object returned from the API
 * @param {Object} fieldMapping - Mapping of address fields to form field names
 * @returns {Object} Formatted data for form
 */
export const googlePlaceToFormData = (place, fieldMapping = {}) => {
  const parsedAddress = parseGoogleAddress(place);
  
  // Default field mapping
  const defaultMapping = {
    streetAddress: 'address',
    city: 'city',
    state: 'state',
    zipCode: 'zip',
    country: 'country'
  };
  
  // Merge with custom field mapping
  const mapping = { ...defaultMapping, ...fieldMapping };
  
  // Create form data object
  const formData = {};
  
  // Map each address field to the corresponding form field
  Object.entries(mapping).forEach(([addressField, formField]) => {
    if (parsedAddress[addressField]) {
      formData[formField] = parsedAddress[addressField];
    }
  });
  
  return formData;
}; 